//30-01-2026
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, Save } from "lucide-react";
import "../../css/document/documentform.css";
import api from "../../api/api";

export default function DocumentForm({ document, onSubmit, onCancel, isLoading }) {
  const [formData, setFormData] = useState(
    document || {
      document_name: "",
      document_type: "kyc",
      dealId: "",
      status: "pending",
      notes: "",
    }
  );

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 🔹 Deals state
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);

  /* ---------------- FETCH DEALS ---------------- */
  useEffect(() => {
    fetchDeals();
  }, []);

  const fetchDeals = async () => {
    try {
      setLoadingDeals(true);
      const res = await api.get("/deals");
      const dealsArray = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];
      setDeals(dealsArray);
    } catch (err) {
      console.error("Failed to fetch deals", err);
      setDeals([]);
    } finally {
      setLoadingDeals(false);
    }
  };

  /* ---------------- FILE SELECTION ---------------- */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);

    // Update document name if empty
    setFormData((prev) => ({
      ...prev,
      document_name: prev.document_name || file.name,
    }));
  };

  /* ---------------- FORM HANDLERS ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile && !document) {
      alert("Please upload a file!");
      return;
    }

    const data = new FormData();
    data.append("document_name", formData.document_name);
    data.append("document_type", formData.document_type);
    data.append("status", formData.status);
    data.append("notes", formData.notes);
    data.append("dealId", formData.dealId);

    if (selectedFile) data.append("file", selectedFile);

    onSubmit(data, setUploadProgress); // pass FormData and progress updater
  };

  return (
    <motion.div
      className="doc-wrapper"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className="doc-card">
        {/* Header */}
        <div className="doc-header">
          <h2>{document ? "Edit Document" : "Upload New Document"}</h2>
          <button className="icon-btn" onClick={onCancel}>
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="doc-body">
            {/* File Upload */}
            <div className="form-group">
              <label>Upload File *</label>
              <input
                type="file"
                onChange={handleFileChange}
                disabled={uploading}
              />
              {uploading && (
                <span className="info-text">
                  Uploading... {uploadProgress}%
                </span>
              )}
              {document?.file_url && !selectedFile && (
                <span className="success-text">✓ File already uploaded</span>
              )}
              {selectedFile && !uploading && (
                <span className="success-text">✓ File selected: {selectedFile.name}</span>
              )}
            </div>

            {/* Document Name */}
            <div className="form-group">
              <label>Document Name *</label>
              <input
                type="text"
                name="document_name"
                value={formData.document_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label>Document Type *</label>
                <select
                  name="document_type"
                  value={formData.document_type}
                  onChange={handleChange}
                >
                  <option value="kyc">KYC</option>
                  <option value="property_papers">Property Papers</option>
                  <option value="agreement">Agreement</option>
                  <option value="offer_letter">Offer Letter</option>
                  <option value="invoice">Invoice</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="pending">Pending</option>
                  <option value="verified">Verified</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            {/* 🔹 Deal Field */}
            <div className="form-group">
              <label>Deal *</label>
              {loadingDeals ? (
                <span className="info-text">Loading deals...</span>
              ) : (
                <select
                  name="dealId"
                  value={formData.dealId}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Deal</option>
                  {deals.map((deal) => (
                    <option key={deal._id} value={deal._id}>
                      {deal.dealName || deal.project_name || "Unnamed Deal"}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Notes */}
            <div className="form-group">
              <label>Notes</label>
              <textarea
                name="notes"
                rows="3"
                value={formData.notes}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="doc-footer">
            <button type="button" className="btn-outline" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              <Save size={16} />
              {isLoading ? "Saving..." : "Save Document"}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}
